// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import { ATTR_CLIENT_ADDRESS, ATTR_CLIENT_PORT, ATTR_ENDUSER_ID, ATTR_ENDUSER_PSEUDO_ID, ATTR_ERROR_TYPE, ATTR_EXCEPTION_MESSAGE, ATTR_EXCEPTION_STACKTRACE, ATTR_EXCEPTION_TYPE, ATTR_HTTP_REQUEST_METHOD, ATTR_HTTP_RESPONSE_STATUS_CODE, ATTR_HTTP_ROUTE, ATTR_NETWORK_LOCAL_ADDRESS, ATTR_NETWORK_LOCAL_PORT, ATTR_NETWORK_PEER_ADDRESS, ATTR_NETWORK_PEER_PORT, ATTR_NETWORK_PROTOCOL_NAME, ATTR_NETWORK_PROTOCOL_VERSION, ATTR_NETWORK_TRANSPORT, ATTR_SERVER_ADDRESS, ATTR_SERVER_PORT, ATTR_URL_FULL, ATTR_URL_PATH, ATTR_URL_QUERY, ATTR_URL_SCHEME, ATTR_USER_AGENT_ORIGINAL, CtxTagKeys, DBSYSTEMVALUES_MONGODB, DBSYSTEMVALUES_MYSQL, DBSYSTEMVALUES_POSTGRESQL, DBSYSTEMVALUES_REDIS, EXP_ATTR_ENDUSER_ID, EXP_ATTR_ENDUSER_PSEUDO_ID, EXP_ATTR_SYNTHETIC_TYPE, MicrosoftClientIp, RemoteDependencyDataType, RemoteDependencyEnvelopeType, RequestDataType, RequestEnvelopeType, SEMATTRS_DB_NAME, SEMATTRS_DB_OPERATION, SEMATTRS_DB_STATEMENT, SEMATTRS_DB_SYSTEM, SEMATTRS_ENDUSER_ID, SEMATTRS_EXCEPTION_MESSAGE, SEMATTRS_EXCEPTION_STACKTRACE, SEMATTRS_EXCEPTION_TYPE, SEMATTRS_HTTP_CLIENT_IP, SEMATTRS_HTTP_FLAVOR, SEMATTRS_HTTP_HOST, SEMATTRS_HTTP_METHOD, SEMATTRS_HTTP_ROUTE, SEMATTRS_HTTP_SCHEME, SEMATTRS_HTTP_STATUS_CODE, SEMATTRS_HTTP_TARGET, SEMATTRS_HTTP_URL, SEMATTRS_HTTP_USER_AGENT, SEMATTRS_NET_HOST_IP, SEMATTRS_NET_HOST_NAME, SEMATTRS_NET_HOST_PORT, SEMATTRS_NET_PEER_IP, SEMATTRS_NET_PEER_NAME, SEMATTRS_NET_PEER_PORT, SEMATTRS_NET_TRANSPORT, SEMATTRS_PEER_SERVICE, SEMATTRS_RPC_GRPC_STATUS_CODE, SEMATTRS_RPC_SYSTEM, createAttributeContainer, createTelemetryItem, fieldRedaction, getDependencyTarget, getHttpMethod, getHttpStatusCode, getHttpUrl, getLocationIp, getUrl, getUserAgent, hrTimeToMilliseconds, isSqlDB, isSyntheticSource, urlGetPathName } from "@microsoft/applicationinsights-core-js";
import { arrIncludes, asString, getLazy, isNullOrUndefined, strLower, strStartsWith } from "@nevware21/ts-utils";
import { STR_EMPTY, UNDEFINED_VALUE } from "../../InternalConstants";
import { _DYN_ATTRIBUTES, _DYN_ATTRIB_CONTAINER, _DYN_BASE_DATA, _DYN_KIND, _DYN_LOGGER, _DYN_RESPONSE_CODE, _DYN_SPAN_CONTEXT, _DYN_SPAN_ID, _DYN_TARGET, _DYN_TRACE_FLAGS, _DYN_TRACE_ID, _DYN_TYPE } from "../../__DynamicConstants";
/**
 * Azure SDK namespace.
 * @internal
 */
var AzNamespace = "az.namespace";
var AzResourceNamespace = "azure.resource_provider.namespace";
/**
 * Azure SDK Eventhub.
 * @internal
 */
var MicrosoftEventHub = "Microsoft.EventHub";
/**
 * Azure SDK message bus destination.
 * @internal
 */
var MessageBusDestination = "message_bus.destination";
/**
 * AI time since enqueued attribute.
 * @internal
 */
var TIME_SINCE_ENQUEUED = "timeSinceEnqueued";
var PORT_REGEX = ( /*#__PURE__*/getLazy(function () { return new RegExp(/(https?)(:\/\/.*)(:\d+)(\S*)/); }));
var HTTP_DOT = ( /*#__PURE__*/"http.");
var _MS_PROCESSED_BY_METRICS_EXTRACTORS = ( /* #__PURE__*/"_MS.ProcessedByMetricExtractors");
/**
 * Legacy HTTP semantic convention values
 * @internal
 */
var _ignoreSemanticValues = ( /* #__PURE__*/getLazy(_initIgnoreSemanticValues));
/* #__NO_SIDE_EFFECTS__ */
function _initIgnoreSemanticValues() {
    return [
        // Internal Microsoft attributes
        _MS_PROCESSED_BY_METRICS_EXTRACTORS,
        // Legacy HTTP semantic values
        SEMATTRS_NET_PEER_IP,
        SEMATTRS_NET_PEER_NAME,
        SEMATTRS_NET_HOST_IP,
        SEMATTRS_PEER_SERVICE,
        SEMATTRS_HTTP_USER_AGENT,
        SEMATTRS_HTTP_METHOD,
        SEMATTRS_HTTP_URL,
        SEMATTRS_HTTP_STATUS_CODE,
        SEMATTRS_HTTP_ROUTE,
        SEMATTRS_HTTP_HOST,
        SEMATTRS_DB_SYSTEM,
        SEMATTRS_DB_STATEMENT,
        SEMATTRS_DB_OPERATION,
        SEMATTRS_DB_NAME,
        SEMATTRS_RPC_SYSTEM,
        SEMATTRS_RPC_GRPC_STATUS_CODE,
        SEMATTRS_EXCEPTION_TYPE,
        SEMATTRS_EXCEPTION_MESSAGE,
        SEMATTRS_EXCEPTION_STACKTRACE,
        SEMATTRS_HTTP_SCHEME,
        SEMATTRS_HTTP_TARGET,
        SEMATTRS_HTTP_FLAVOR,
        SEMATTRS_NET_TRANSPORT,
        SEMATTRS_NET_HOST_NAME,
        SEMATTRS_NET_HOST_PORT,
        SEMATTRS_NET_PEER_PORT,
        SEMATTRS_HTTP_CLIENT_IP,
        SEMATTRS_ENDUSER_ID,
        HTTP_DOT + "status_text",
        // http Semabtic conventions
        ATTR_CLIENT_ADDRESS,
        ATTR_CLIENT_PORT,
        ATTR_SERVER_ADDRESS,
        ATTR_SERVER_PORT,
        ATTR_URL_FULL,
        ATTR_URL_PATH,
        ATTR_URL_QUERY,
        ATTR_URL_SCHEME,
        ATTR_ERROR_TYPE,
        ATTR_NETWORK_LOCAL_ADDRESS,
        ATTR_NETWORK_LOCAL_PORT,
        ATTR_NETWORK_PROTOCOL_NAME,
        ATTR_NETWORK_PEER_ADDRESS,
        ATTR_NETWORK_PEER_PORT,
        ATTR_NETWORK_PROTOCOL_VERSION,
        ATTR_NETWORK_TRANSPORT,
        ATTR_USER_AGENT_ORIGINAL,
        ATTR_HTTP_REQUEST_METHOD,
        ATTR_HTTP_RESPONSE_STATUS_CODE,
        ATTR_EXCEPTION_TYPE,
        ATTR_EXCEPTION_MESSAGE,
        ATTR_EXCEPTION_STACKTRACE,
        EXP_ATTR_ENDUSER_ID,
        EXP_ATTR_ENDUSER_PSEUDO_ID,
        EXP_ATTR_SYNTHETIC_TYPE
    ];
}
function _populateTagsFromSpan(telemetryItem, span, contextKeys, config) {
    var tags = telemetryItem.tags = (telemetryItem.tags || []);
    var container = span[_DYN_ATTRIB_CONTAINER /* @min:%2eattribContainer */] || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    tags[contextKeys.operationId] = span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]()[_DYN_TRACE_ID /* @min:%2etraceId */];
    if ((span.parentSpanContext || {})[_DYN_SPAN_ID /* @min:%2espanId */]) {
        tags[contextKeys.operationParentId] = span.parentSpanContext[_DYN_SPAN_ID /* @min:%2espanId */];
    }
    // Map OpenTelemetry enduser attributes to Application Insights user attributes
    var endUserId = container.get(ATTR_ENDUSER_ID);
    if (endUserId) {
        tags[contextKeys.userAuthUserId] = asString(endUserId);
    }
    var endUserPseudoId = container.get(ATTR_ENDUSER_PSEUDO_ID);
    if (endUserPseudoId) {
        tags[contextKeys.userId] = asString(endUserPseudoId);
    }
    var httpUserAgent = getUserAgent(container);
    if (httpUserAgent) {
        tags["ai.user.userAgent"] = String(httpUserAgent);
    }
    if (isSyntheticSource(container)) {
        tags[contextKeys.operationSyntheticSource] = "True";
    }
    // Check for microsoft.client.ip first - this takes precedence over all other IP logic
    var microsoftClientIp = container.get(MicrosoftClientIp);
    if (microsoftClientIp) {
        tags[contextKeys.locationIp] = asString(microsoftClientIp);
    }
    if (span[_DYN_KIND /* @min:%2ekind */] === 1 /* eOTelSpanKind.SERVER */) {
        var httpMethod = getHttpMethod(container);
        // Only use the fallback IP logic for server spans if microsoft.client.ip is not set
        if (!microsoftClientIp) {
            tags[contextKeys.locationIp] = getLocationIp(container);
        }
        if (httpMethod) {
            var httpRoute = container.get(ATTR_HTTP_ROUTE);
            var httpUrl = getHttpUrl(container);
            tags[contextKeys.operationName] = span.name; // Default
            if (httpRoute) {
                // AiOperationName max length is 1024
                // https://github.com/MohanGsk/ApplicationInsights-Home/blob/master/EndpointSpecs/Schemas/Bond/ContextTagKeys.bond
                tags[contextKeys.operationName] = httpMethod + " " + fieldRedaction(asString(httpRoute), config);
            }
            else if (httpUrl) {
                try {
                    var urlPathName = fieldRedaction(urlGetPathName(asString(httpUrl)), config);
                    tags[contextKeys.operationName] = httpMethod + " " + urlPathName;
                }
                catch (_a) {
                    /* no-op */
                }
            }
        }
        else {
            tags[contextKeys.operationName] = span.name;
        }
    }
    else {
        var opName = container.get(contextKeys.operationName);
        if (opName) {
            tags[contextKeys.operationName] = opName;
        }
    }
    // TODO: Location IP TBD for non server spans
}
/**
 * Check to see if the key is in the list of known properties to ignore (exclude)
 * from the properties collection
 * @param key - the property key to check
 * @param contextKeys - The current context keys
 * @returns true if the key should be ignored, false otherwise
 */
function _isIgnorePropertiesKey(key, contextKeys) {
    var result = false;
    if (arrIncludes(_ignoreSemanticValues.v, key)) {
        // The key is in set of known keys to ignore
        result = true;
    }
    else if (strStartsWith(key, "microsoft.")) {
        // Ignoring all ALL keys starting with "microsoft."
        result = true;
    }
    else if (key === contextKeys.operationName) {
        // Ignoring the key if it is the operation name context tag
        result = true;
    }
    return result;
}
function _populatePropertiesFromAttributes(item, contextKeys, container) {
    if (container) {
        var baseData = item[_DYN_BASE_DATA /* @min:%2ebaseData */] = (item[_DYN_BASE_DATA /* @min:%2ebaseData */] || {});
        var properties_1 = baseData.properties = (baseData.properties || {});
        container.forEach(function (key, value) {
            // Avoid duplication ignoring fields already mapped.
            if (!_isIgnorePropertiesKey(key, contextKeys)) {
                properties_1[key] = value;
            }
        });
    }
}
function _populateHttpDependencyProperties(dependencyTelemetry, container, httpMethod, config) {
    if (httpMethod) {
        // HTTP Dependency
        var httpUrl = getHttpUrl(container);
        if (httpUrl) {
            try {
                var pathName = urlGetPathName(asString(httpUrl));
                if (pathName) {
                    dependencyTelemetry.name = httpMethod + " " + fieldRedaction(pathName, config);
                }
            }
            catch (_a) {
                /* no-op */
            }
        }
        dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "Http" /* eDependencyTypes.Http */;
        dependencyTelemetry.data = fieldRedaction(getUrl(container), config);
        var httpStatusCode = getHttpStatusCode(container);
        if (httpStatusCode) {
            dependencyTelemetry[_DYN_RESPONSE_CODE /* @min:%2eresponseCode */] = +httpStatusCode;
        }
        var target = getDependencyTarget(container);
        if (target) {
            try {
                // Remove default port
                var res = PORT_REGEX.v.exec(target);
                if (res !== null) {
                    var protocol = res[1];
                    var port = res[3];
                    if ((protocol === "https" && port === ":443") ||
                        (protocol === "http" && port === ":80")) {
                        // Drop port
                        target = res[1] + res[2] + res[4];
                    }
                }
            }
            catch (_b) {
                /* no-op */
            }
            dependencyTelemetry[_DYN_TARGET /* @min:%2etarget */] = target;
        }
    }
    return !!httpMethod;
}
function _populateDbDependencyProperties(dependencyTelemetry, container, dbSystem) {
    if (dbSystem) {
        // TODO: Remove special logic when Azure UX supports OpenTelemetry dbSystem
        if (String(dbSystem) === DBSYSTEMVALUES_MYSQL) {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "mysql";
        }
        else if (String(dbSystem) === DBSYSTEMVALUES_POSTGRESQL) {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "postgresql";
        }
        else if (String(dbSystem) === DBSYSTEMVALUES_MONGODB) {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "mongodb";
        }
        else if (String(dbSystem) === DBSYSTEMVALUES_REDIS) {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "redis";
        }
        else if (isSqlDB(String(dbSystem))) {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "SQL";
        }
        else {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = String(dbSystem);
        }
        var dbStatement = container.get(SEMATTRS_DB_STATEMENT);
        var dbOperation = container.get(SEMATTRS_DB_OPERATION);
        if (dbStatement) {
            dependencyTelemetry.data = String(dbStatement);
        }
        else if (dbOperation) {
            dependencyTelemetry.data = String(dbOperation);
        }
        var target = getDependencyTarget(container);
        var dbName = container.get(SEMATTRS_DB_NAME);
        if (target) {
            dependencyTelemetry[_DYN_TARGET /* @min:%2etarget */] = dbName ? target + "|" + asString(dbName) : target;
        }
        else {
            dependencyTelemetry[_DYN_TARGET /* @min:%2etarget */] = dbName ? asString(dbName) : asString(dbSystem);
        }
    }
    return !!dbSystem;
}
function _populateRpcDependencyProperties(dependencyTelemetry, container, rpcSystem) {
    if (rpcSystem) {
        if (strLower(rpcSystem) === "wcf") {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "WCF Service" /* eDependencyTypes.Wcf */;
        }
        else {
            dependencyTelemetry[_DYN_TYPE /* @min:%2etype */] = "GRPC" /* eDependencyTypes.Grpc */;
        }
        var grpcStatusCode = container.get(SEMATTRS_RPC_GRPC_STATUS_CODE);
        if (!isNullOrUndefined(grpcStatusCode)) {
            dependencyTelemetry[_DYN_RESPONSE_CODE /* @min:%2eresponseCode */] = +grpcStatusCode;
        }
        var target = getDependencyTarget(container);
        if (target) {
            dependencyTelemetry[_DYN_TARGET /* @min:%2etarget */] = asString(target);
        }
        else {
            dependencyTelemetry[_DYN_TARGET /* @min:%2etarget */] = String(rpcSystem);
        }
    }
    return !!rpcSystem;
}
function createDependencyTelemetryItem(core, span, contextKeys) {
    var container = span[_DYN_ATTRIB_CONTAINER /* @min:%2eattribContainer */] || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    var dependencyType = "Dependency";
    if (span[_DYN_KIND /* @min:%2ekind */] === 3 /* eOTelSpanKind.PRODUCER */) {
        dependencyType = "Queue Message" /* eDependencyTypes.QueueMessage */;
    }
    else if (span[_DYN_KIND /* @min:%2ekind */] === 0 /* eOTelSpanKind.INTERNAL */ && span.parentSpanContext) {
        dependencyType = "InProc" /* eDependencyTypes.InProc */;
    }
    var spanCtx = span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]();
    var dependencyTelemetry = {
        name: span.name,
        id: spanCtx[_DYN_SPAN_ID /* @min:%2espanId */] || core.getTraceCtx()[_DYN_SPAN_ID /* @min:%2espanId */],
        success: (span.status || {}).code !== 2 /* eOTelSpanStatusCode.ERROR */,
        responseCode: 0,
        type: dependencyType,
        duration: hrTimeToMilliseconds(span.duration),
        data: STR_EMPTY,
        target: STR_EMPTY,
        properties: UNDEFINED_VALUE,
        measurements: UNDEFINED_VALUE
    };
    // Check for HTTP Dependency
    if (!_populateHttpDependencyProperties(dependencyTelemetry, container, getHttpMethod(container), core.config)) {
        // Check for DB Dependency
        if (!_populateDbDependencyProperties(dependencyTelemetry, container, container.get(SEMATTRS_DB_SYSTEM))) {
            // Check for Rpc Dependency
            _populateRpcDependencyProperties(dependencyTelemetry, container, container.get(SEMATTRS_RPC_SYSTEM));
        }
    }
    return createTelemetryItem(dependencyTelemetry, RemoteDependencyDataType, RemoteDependencyEnvelopeType.replace("{0}.", ""), core[_DYN_LOGGER /* @min:%2elogger */]);
}
function createRequestTelemetryItem(core, span, contextKeys) {
    var container = span[_DYN_ATTRIB_CONTAINER /* @min:%2eattribContainer */] || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    var spanCtx = span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]();
    var requestData = {
        name: span.name,
        id: spanCtx[_DYN_SPAN_ID /* @min:%2espanId */] || core.getTraceCtx()[_DYN_SPAN_ID /* @min:%2espanId */],
        success: span.status.code !== 0 /* eOTelSpanStatusCode.UNSET */
            ? span.status.code === 1 /* eOTelSpanStatusCode.OK */
            : (Number(getHttpStatusCode(container)) || 0) < 400,
        responseCode: 0,
        duration: hrTimeToMilliseconds(span.duration),
        source: undefined
    };
    var httpMethod = getHttpMethod(container);
    var grpcStatusCode = container.get(SEMATTRS_RPC_GRPC_STATUS_CODE);
    if (httpMethod) {
        requestData.url = fieldRedaction(getUrl(container), core.config);
        var httpStatusCode = getHttpStatusCode(container);
        if (httpStatusCode) {
            requestData[_DYN_RESPONSE_CODE /* @min:%2eresponseCode */] = +httpStatusCode;
        }
    }
    else if (grpcStatusCode) {
        requestData[_DYN_RESPONSE_CODE /* @min:%2eresponseCode */] = +grpcStatusCode;
    }
    return createTelemetryItem(requestData, RequestDataType, RequestEnvelopeType.replace("{0}.", ""), core[_DYN_LOGGER /* @min:%2elogger */]);
}
export function createTelemetryItemFromSpan(core, span) {
    var telemetryItem = null;
    var container = span[_DYN_ATTRIB_CONTAINER /* @min:%2eattribContainer */] || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    var contextKeys = CtxTagKeys;
    var kind = span[_DYN_KIND /* @min:%2ekind */];
    if (kind == 1 /* eOTelSpanKind.SERVER */ || kind == 4 /* eOTelSpanKind.CONSUMER */) {
        // Request
        telemetryItem = createRequestTelemetryItem(core, span, contextKeys);
    }
    else if (kind == 2 /* eOTelSpanKind.CLIENT */ || kind == 3 /* eOTelSpanKind.PRODUCER */ || kind == 0 /* eOTelSpanKind.INTERNAL */) {
        // RemoteDependency
        telemetryItem = createDependencyTelemetryItem(core, span, contextKeys);
    }
    else {
        //diag.error(`Unsupported span kind ${span.kind}`);
    }
    if (telemetryItem) {
        // Set start time for the telemetry item from the event, not the time it is being processed (the default)
        // The channel envelope creator uses this value when creating the envelope only when defined, otherwise it
        // uses the time when the item is being processed
        var baseData = telemetryItem[_DYN_BASE_DATA /* @min:%2ebaseData */] = telemetryItem[_DYN_BASE_DATA /* @min:%2ebaseData */] || {};
        baseData.startTime = new Date(hrTimeToMilliseconds(span.startTime));
        // Add dt extension to the telemetry item
        var ext = telemetryItem.ext = telemetryItem.ext || {};
        var dt = ext["dt"] = ext["dt"] || {};
        // Don't overwrite any existing values
        dt.spanId = dt.spanId || span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]()[_DYN_SPAN_ID /* @min:%2espanId */];
        dt.traceId = dt.traceId || span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]()[_DYN_TRACE_ID /* @min:%2etraceId */];
        var traceFlags = span[_DYN_SPAN_CONTEXT /* @min:%2espanContext */]()[_DYN_TRACE_FLAGS /* @min:%2etraceFlags */];
        if (!isNullOrUndefined(traceFlags)) {
            dt[_DYN_TRACE_FLAGS /* @min:%2etraceFlags */] = dt[_DYN_TRACE_FLAGS /* @min:%2etraceFlags */] || traceFlags;
        }
        _populateTagsFromSpan(telemetryItem, span, contextKeys, core.config);
        _populatePropertiesFromAttributes(telemetryItem, contextKeys, container);
        var sampleRate = container.get("microsoft.sample_rate");
        if (!isNullOrUndefined(sampleRate)) {
            telemetryItem.sampleRate = Number(sampleRate);
        }
        // Azure SDK
        var azNamespace = container.get(AzResourceNamespace) || container.get(AzNamespace);
        if (azNamespace) {
            if (span[_DYN_KIND /* @min:%2ekind */] === 0 /* eOTelSpanKind.INTERNAL */) {
                baseData[_DYN_TYPE /* @min:%2etype */] = "InProc" /* eDependencyTypes.InProc */ + " | " + azNamespace;
            }
            if (azNamespace === MicrosoftEventHub) {
                _parseEventHubSpan(telemetryItem, span);
            }
        }
    }
    return telemetryItem;
}
/**
 * Implementation of Mapping to Azure Monitor
 *
 * https://gist.github.com/lmolkova/e4215c0f44a49ef824983382762e6b92#mapping-to-azure-monitor-application-insights-telemetry
 * @internal
 */
function _parseEventHubSpan(telemetryItem, span) {
    var baseData = telemetryItem[_DYN_BASE_DATA /* @min:%2ebaseData */] = telemetryItem[_DYN_BASE_DATA /* @min:%2ebaseData */] || {};
    var container = span[_DYN_ATTRIB_CONTAINER /* @min:%2eattribContainer */] || createAttributeContainer(span[_DYN_ATTRIBUTES /* @min:%2eattributes */]);
    var namespace = container.get(AzResourceNamespace) || container.get(AzNamespace);
    var peerAddress = asString(container.get(SEMATTRS_NET_PEER_NAME) || container.get("peer.address") || "unknown").replace(/\/$/g, ""); // remove trailing "/"
    var messageBusDestination = (container.get(MessageBusDestination) || "unknown");
    var baseType = baseData[_DYN_TYPE /* @min:%2etype */] || "";
    var kind = span[_DYN_KIND /* @min:%2ekind */];
    if (kind === 2 /* eOTelSpanKind.CLIENT */) {
        baseType = namespace;
        baseData[_DYN_TARGET /* @min:%2etarget */] = peerAddress + "/" + messageBusDestination;
    }
    else if (kind === 3 /* eOTelSpanKind.PRODUCER */) {
        baseType = "Queue Message | " + namespace;
        baseData[_DYN_TARGET /* @min:%2etarget */] = peerAddress + "/" + messageBusDestination;
    }
    else if (kind === 4 /* eOTelSpanKind.CONSUMER */) {
        baseType = "Queue Message | " + namespace;
        baseData.source = peerAddress + "/" + messageBusDestination;
        var measurements = baseData.measurements = (baseData.measurements || {});
        var timeSinceEnqueued = container.get("timeSinceEnqueued");
        if (timeSinceEnqueued) {
            measurements[TIME_SINCE_ENQUEUED] = Number(timeSinceEnqueued);
        }
        else {
            var enqueuedTime = parseFloat(asString(container.get("enqueuedTime")));
            if (isNaN(enqueuedTime)) {
                enqueuedTime = 0;
            }
            measurements[TIME_SINCE_ENQUEUED] = hrTimeToMilliseconds(span.startTime) - enqueuedTime;
        }
    }
    baseData[_DYN_TYPE /* @min:%2etype */] = baseType;
}
//# sourceMappingURL=spanUtils.js.map